import type { CSSProperties, ReactNode } from 'react';
import { AnimatedNumber, Sparkline } from './telemetry';
import { Caption } from './primitives';

/**
 * KPI tile — chiffre principal + sparkline + delta + target + min/max.
 *
 * Utilisé dans Mission Control (4 tiles), Parent Dashboard (6 tiles).
 * Tabular numerics partout pour que les chiffres ne dansent pas.
 */

export function KPI({
  label,
  value,
  format = (v) => String(Math.round(v)),
  delta,
  deltaTone = 'volt',
  target,
  series,
  color = 'var(--acid-500)',
  minMax,
  children,
  delay,
}: {
  label: string;
  value: number;
  format?: (v: number) => string;
  delta?: string;
  deltaTone?: 'volt' | 'coral' | 'amber' | 'cyan' | 'violet';
  target?: string;
  series?: number[];
  color?: string;
  minMax?: [number | string, number | string, string?];
  children?: ReactNode;
  /** Délai (ms) avant l'animation card-enter — utilisé pour stagger un grid. */
  delay?: number;
}): JSX.Element {
  const deltaColor = {
    volt: 'text-volt-500',
    coral: 'text-coral-500',
    amber: 'text-amber-500',
    cyan: 'text-cyan-500',
    violet: 'text-violet-500',
  }[deltaTone];
  const enterStyle: CSSProperties | undefined =
    delay !== undefined ? { animationDelay: `${delay}ms` } : undefined;
  return (
    <div
      className="bg-ink-800 border border-ink-700 rounded-xl p-5 flex flex-col gap-2 justify-between animate-card-enter"
      style={enterStyle}
    >
      <div>
        <Caption>{label}</Caption>
        <div className="flex items-center justify-between gap-3 mt-1">
          <AnimatedNumber
            value={value}
            format={format}
            className="font-mono"
            style={{ fontSize: 28, color: 'var(--paper-50)', lineHeight: 1, fontWeight: 500 }}
          />
          {series && <Sparkline data={series} color={color} width={96} height={32} />}
        </div>
        <div className="flex items-center gap-1.5 text-[11px] font-mono whitespace-nowrap mt-1.5">
          {delta && <span className={deltaColor}>{delta}</span>}
          {target && <span className="text-ink-300">· cible {target}</span>}
        </div>
      </div>
      {minMax && (
        <div className="flex items-center gap-3 pt-2 border-t border-ink-700 font-mono text-[11px] text-ink-300">
          <span>
            min <span className="text-paper-50">{minMax[0]}</span>
          </span>
          <span className="text-ink-700">·</span>
          <span>
            max <span className="text-paper-50">{minMax[1]}</span>
          </span>
          {minMax[2] && <span className="ml-auto">{minMax[2]}</span>}
        </div>
      )}
      {children}
    </div>
  );
}

// ─── ModuleTile ────────────────────────────────────────────────────────────

export function ModuleTile({
  label,
  tagline,
  color,
  progress,
  footer,
  disabled = false,
  disabledLabel,
  onClick,
  icon,
  delay,
}: {
  label: string;
  tagline: string;
  color: string;
  progress: number; // 0..100
  footer?: string;
  disabled?: boolean;
  disabledLabel?: string;
  onClick?: () => void;
  icon?: ReactNode;
  /** Délai (ms) avant l'animation card-enter — utilisé pour stagger un grid. */
  delay?: number;
}): JSX.Element {
  const enterStyle: CSSProperties = {
    ...(disabled ? {} : { ['--accent' as never]: color }),
    ...(delay !== undefined ? { animationDelay: `${delay}ms` } : {}),
  };
  return (
    <div
      onClick={disabled ? undefined : onClick}
      className={`bg-ink-800 border border-ink-700 rounded-xl p-5 flex flex-col gap-3 transition-all duration-150 ${
        disabled
          ? 'opacity-60'
          : 'cursor-pointer hover:border-opacity-60 hover:shadow-card-hover'
      } animate-card-enter`}
      style={enterStyle}
    >
      <div className="flex items-center justify-between">
        <div
          className="inline-flex items-center justify-center w-9 h-9 rounded-lg border"
          style={{ color, borderColor: `${color}33`, backgroundColor: `${color}10` }}
        >
          {icon ?? <span className="font-mono text-xs">{label.slice(0, 1)}</span>}
        </div>
        {disabled ? (
          <span className="text-[10px] font-mono uppercase text-ink-300 tracking-widest">{disabledLabel ?? 'bientôt'}</span>
        ) : (
          // PulseDot proto = ring qui s'étale (cf. telemetry.tsx). Ici on garde le
          // dot inline + box-shadow accent module pour signaler le module vivant.
          <span
            className="relative inline-flex items-center justify-center size-2"
            aria-hidden="true"
          >
            <span
              className="absolute inset-0 rounded-full animate-pulse-ring"
              style={{ backgroundColor: color }}
            />
            <span
              className="relative size-2 rounded-full"
              style={{ backgroundColor: color, boxShadow: `0 0 0 1.5px ${color}33` }}
            />
          </span>
        )}
      </div>
      <div>
        <span
          className="text-caption uppercase block"
          style={{ color, letterSpacing: '0.05em', fontWeight: 500 }}
        >
          {label}
        </span>
        <p className="mt-1 text-sm text-paper-50/80 italic">{tagline}</p>
      </div>
      <div className="mt-auto flex flex-col gap-1.5">
        <div className="h-1 bg-ink-700 rounded-full overflow-hidden">
          <div
            className="h-full transition-all duration-300"
            style={{ width: disabled ? '0%' : `${progress}%`, backgroundColor: color }}
          />
        </div>
        <div className="flex justify-between font-mono text-[11px] text-ink-300">
          <span>{footer}</span>
          <span style={{ color: disabled ? 'var(--ink-300)' : color }}>
            {disabled ? '—' : `${progress}% maîtrise`}
          </span>
        </div>
      </div>
    </div>
  );
}
